import React, { useState, useMemo, useEffect } from 'react';
import s from './MindMapContent.module.scss';
import MindMap from '@/components/MindMap';

/**
 * 思维导图内容组件
 * 用于展示思维导图图片，支持下载
 */
const formatMindmapText = (text = '') => {
    if (!text) return '';
    const trimmed = text.trim();
    if (trimmed.startsWith('```')) {
        return trimmed
            .replace(/^```mindmap\s*/i, '')
            .replace(/^```/, '')
            .replace(/```$/, '')
            .trim();
    }
    return trimmed;
};

// 解析简单的 markdown / 列表结构为树状节点
const parseMindmap = (text = '') => {
    if (!text) return [];

    const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => !!line);

    if (!lines.length) return [];

    const nodes = [];
    const lastNodeAtDepth = {};

    lines.forEach((line, index) => {
        let depth = 1;
        let label = line;

        // 自定义规则（注意顺序：先匹配 ###，再匹配 ##，避免 ### 被识别成 ##）
        // ## 标题 => 一级节点
        // ### 标题 => 二级节点
        // - 文本   => 二级节点下的内容（作为第三级）
        if (/^###\s+/.test(line)) {
            depth = 2;
            label = line.replace(/^###\s+/, '');
        } else if (/^##\s+/.test(line)) {
            depth = 1;
            label = line.replace(/^##\s+/, '');
        } else if (/^-\s+/.test(line)) {
            depth = 3;
            label = line.replace(/^-\s+/, '');
        } else {
            // 其余行忽略，避免噪声
            return;
        }

        const node = {
            id: index,
            depth,
            label
        };

        // 计算 parentId：
        if (depth === 1) {
            node.parentId = undefined;
        } else {
            let parent = lastNodeAtDepth[depth - 1];
            let searchDepth = depth - 1;

            // 如果没有直接上一级，向上寻找更高层级
            while (!parent && searchDepth > 0) {
                searchDepth -= 1;
                parent = lastNodeAtDepth[searchDepth];
            }

            if (parent) {
                node.parentId = parent.id;
            } else {
                // 仍然没有找到上级，则挂到最早的一级节点
                const firstLevelNode = nodes.find((n) => n.depth === 1);
                node.parentId = firstLevelNode ? firstLevelNode.id : undefined;
            }
        }

        nodes.push(node);
        lastNodeAtDepth[depth] = node;
    });

    return nodes;
};

const MindMapContent = ({ data, disableInteraction = false }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // 图片加载成功
    const handleImageLoad = () => {
        setImageLoaded(true);
        setImageError(false);
    };

    // 图片加载失败
    const handleImageError = () => {
        setImageLoaded(false);
        setImageError(true);
    };

    const hasImage = !!data.imageUrl && !imageError;
    const mindmapText = formatMindmapText(data.content || data.description);

    // 切换数据时重置图片状态，避免残留的加载状态
    useEffect(() => {
        setImageLoaded(false);
        setImageError(false);
    }, [data.imageUrl, mindmapText]);

    // 将解析后的节点转换为 MindMap 组件需要的树结构
    const mindmapTree = useMemo(() => {
        if (!mindmapText || hasImage) return null;
        const nodes = parseMindmap(mindmapText);
        if (!nodes.length) return null;

        const nodeMap = new Map();
        nodes.forEach((n) => {
            nodeMap.set(n.id, { title: n.label, children: [] });
        });

        const roots = [];
        nodes.forEach((n) => {
            const current = nodeMap.get(n.id);
            if (n.parentId != null && nodeMap.has(n.parentId)) {
                const parent = nodeMap.get(n.parentId);
                parent.children.push(current);
            } else {
                roots.push(current);
            }
        });

        const rootTitle = data.title && data.title !== '思维导图' ? data.title : roots[0]?.title;

        // 如果只有一个根节点且标题与根同名，直接展开其 children，避免重复的最外层节点
        if (roots.length === 1 && roots[0]?.title === rootTitle) {
            return {
                title: rootTitle,
                children: roots[0]?.children || []
            };
        }

        // 若存在与根标题同名的一级节点，移除该重复节点，避免叠加两份
        const filteredRoots = roots.filter((item) => item.title !== rootTitle);

        return {
            title: rootTitle,
            children: filteredRoots.length ? filteredRoots : roots
        };
    }, [mindmapText, hasImage, data.title, data.imageUrl]);

    // 为思维导图组件生成 key，确保切换数据时强制重渲染，避免叠加旧图
    const mindmapKey = useMemo(
        () => `${data.title || 'mindmap'}-${mindmapText || ''}-${data.imageUrl || ''}`,
        [data.title, mindmapText, data.imageUrl]
    );
    console.log(mindmapTree, 'mindmapTree');
    console.log(mindmapTree.title, 'mindmapTree.title');

    return (
        <div className={s['mindmap-container']}>
            {/* 思维导图图片展示 */}
            <div className={s['mindmap-wrapper']}>
                {hasImage ? (
                    <>
                        {!imageLoaded && (
                            <div className={s['loading-placeholder']}>
                                <div className={s['loading-spinner']}></div>
                                <p>加载中...</p>
                            </div>
                        )}
                        <img
                            src={data.imageUrl || '/default-mindmap.webp'}
                            alt={data.title || '思维导图'}
                            className={s['mindmap-image']}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            style={{ display: imageLoaded ? 'block' : 'none' }}
                        />
                    </>
                ) : mindmapTree ? (
                    <MindMap
                        key={mindmapKey}
                        mindMapSummaryListData={mindmapTree.children}
                        courseName={mindmapTree.title}
                        width={600}
                        height={640}
                        size="16"
                        disableInteraction={disableInteraction}
                    />
                ) : mindmapText ? (
                    <div className={s['mindmap-text']}>
                        <pre>{mindmapText}</pre>
                    </div>
                ) : (
                    <div className={s['error-placeholder']}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <p>图片加载失败</p>
                        <span>请稍后重试</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MindMapContent;
